import React, { useEffect, useState } from "react";
import TextField from "@mui/material/TextField";
import Box from "@mui/material/Box";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import axios from "axios";
import { useMediaQuery } from "react-responsive";
import AlertShow from "../AlertShow";
import configData from "../../../config.json";

const doctorTypes = [
  "Gynecologist",
  "Yoga Expert",
  "General Physician",
  "Dietitian",
  "Clinical Psychologist",
  "Pathology",
  "Sonography",
];

function Notes({ setExpand, setSelected }) {
  const isTablet = useMediaQuery({ query: "(min-width: 768px) and (max-width: 1023px)" });
  const isMobile = useMediaQuery({ query: "(max-width: 767px)" });

  const [patientNotes, setPatientNotes] = useState([]);
  const [mode, setMode] = useState("list"); // list | create | view | edit
  const [activeNote, setActiveNote] = useState(null);

  const [subject, setSubject] = useState("");
  const [content, setContent] = useState("");
  const [doctorType, setDoctorType] = useState("");

  const [showAlert, setShowAlert] = useState(false);
  const [alertMsg, setAlertMsg] = useState("");
  const [severity, setSeverity] = useState("");

  const wrapper = {
    marginLeft: isMobile ? "12px" : isTablet ? "20px" : "50px",
    marginRight: isMobile ? "12px" : isTablet ? "20px" : "50px",
    paddingLeft: isMobile ? "12px" : isTablet ? "20px" : "30px",
    paddingRight: isMobile ? "12px" : "20px",
    paddingBottom: "28px",
    background: "#FBFBFB",
    borderRadius: "12px",
    paddingTop: "24px",
    marginBottom: "50px",
  };

  const sectionBox = {
    background: "#FFFFFF",
    border: "1px solid #E3E3E5",
    borderRadius: "10px",
    padding: isMobile ? "12px" : "16px",
  };

  const cardGrid = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit, minmax(220px, 1fr))",
    gap: "12px",
  };

  const noteCard = {
    border: "1px solid #E3E3E5",
    borderRadius: "10px",
    padding: "12px",
    background: "#fff",
    minHeight: "130px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    cursor: "pointer",
    boxShadow: "0 2px 6px rgba(0,0,0,0.05)",
  };

  const createCard = {
    ...noteCard,
    border: "1px dashed #004AAD",
    background: "#F7FAFF",
    alignItems: "center",
    justifyContent: "center",
    color: "#004AAD",
  };

  const primaryBtn = {
    minWidth: isMobile ? "100%" : "96px",
    height: isMobile ? "40px" : "36px",
    borderRadius: "6px",
    border: "none",
    background: "#004AAD",
    color: "#fff",
    cursor: "pointer",
    padding: "0 12px",
    fontWeight: 600,
  };

  const secondaryBtn = {
    ...primaryBtn,
    background: "#fff",
    border: "1px solid #D9D9D9",
    color: "#333",
  };

  const dangerBtn = {
    ...primaryBtn,
    background: "#E42346",
  };

  const actionRow = {
    display: "flex",
    gap: "10px",
    flexDirection: isMobile ? "column" : "row",
    flexWrap: "wrap",
  };

  const noteTextBox = {
    marginTop: "14px",
    padding: isMobile ? "12px" : "14px",
    border: "1px solid #E3E3E5",
    borderRadius: "8px",
    background: "#FAFAFA",
    whiteSpace: "pre-wrap",
    lineHeight: 1.6,
    minHeight: isMobile ? "180px" : "240px",
    wordBreak: "break-word",
  };

  const handleAlertMsg = (errorType, msg) => {
    setShowAlert(true);
    setSeverity(errorType);
    setAlertMsg(msg);
    setTimeout(() => {
      setShowAlert(false);
    }, 2500);
  };

  const handleCloseAlert = () => {
    setShowAlert(false);
  };

  const loadNotes = async () => {
    try {
      const response = await axios.post(
        `${configData.SERVER_API_URL}/patient/get_patient_notes`,
        { patient_id: localStorage.getItem("userId") },
      );
      setPatientNotes(response?.data?.Notes || []);
    } catch {
      handleAlertMsg("error", "Unable to fetch notes");
    }
  };

  useEffect(() => {
    loadNotes();
  }, []);

  const resetForm = () => {
    setSubject("");
    setContent("");
    setDoctorType("");
  };

  const getPreview = (value) => {
    if (!value) return "";
    const plain = String(value).replace(/\s+/g, " ").trim();
    if (plain.length <= 75) return plain;
    return `${plain.slice(0, 75)}...`;
  };

  const openCreate = () => {
    resetForm();
    setActiveNote(null);
    setMode("create");
  };

  const openNote = (note) => {
    setActiveNote(note);
    setMode("view");
  };

  const openEdit = () => {
    if (!activeNote) return;
    setSubject(activeNote.title || "");
    setContent(activeNote.content || "");
    setDoctorType(activeNote.doctor_type || "");
    setMode("edit");
  };

  const goToList = () => {
    setMode("list");
  };

  const handleSave = async () => {
    if (!subject?.trim() || !doctorType || !content?.trim()) {
      handleAlertMsg("warning", "Please fill subject, doctor type and notes");
      return;
    }

    try {
      const apiUrl = mode === "edit"
        ? `${configData.SERVER_API_URL}/patient/update_patient_notes`
        : `${configData.SERVER_API_URL}/patient/add_patient_notes`;

      const requestBody = {
        title: subject,
        doctor_type: doctorType,
        content,
        patient_id: localStorage.getItem("userId"),
      };

      if (mode === "edit" && activeNote?.id) {
        requestBody.id = activeNote.id;
      }

      await axios.post(apiUrl, requestBody);
      handleAlertMsg("success", "Note saved successfully");
      await loadNotes();
      resetForm();
      setActiveNote(null);
      setMode("list");
    } catch {
      handleAlertMsg("error", "Unable to save note");
    }
  };

  const handleDelete = async () => {
    if (!activeNote?.id) return;

    try {
      await axios.post(`${configData.SERVER_API_URL}/patient/delete_patient_notes`, {
        id: activeNote.id,
        patient_id: localStorage.getItem("userId"),
      });

      handleAlertMsg("success", "Note deleted successfully");
      setPatientNotes((prev) => prev.filter((item) => item.id !== activeNote.id));
      setActiveNote(null);
      setMode("list");
    } catch {
      handleAlertMsg("error", "Unable to delete note");
    }
  };

  const renderList = () => (
    <div style={sectionBox}>
      <div style={cardGrid}>
        <div style={createCard} onClick={openCreate}>
          <img
            src="/GarbhaSanskar/plus.png"
            style={{ height: "38px", width: "38px" }}
            alt="Create New Note"
          />
          <div style={{ marginTop: "8px", fontWeight: 600 }}>Create New Note</div>
        </div>

        {patientNotes.map((item) => (
          <div key={item.id} style={noteCard} onClick={() => openNote(item)}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <img
                  src="/GarbhaSanskar/notes.png"
                  alt="Note"
                  style={{ width: "18px", height: "18px" }}
                />
                <div style={{ fontWeight: 600, fontSize: "14px" }}>{item.title}</div>
              </div>
              <div style={{ fontSize: "12px", color: "#666", marginTop: "8px" }}>
                {item.doctor_type}
              </div>
              <div style={{ fontSize: "12px", color: "#777", marginTop: "6px" }}>
                {getPreview(item.content)}
              </div>
            </div>
            <div style={{ marginTop: "10px", color: "#004AAD", fontWeight: 600, fontSize: "12px" }}>
              Open Note
            </div>
          </div>
        ))}
      </div>

      {!patientNotes.length && (
        <div style={{ marginTop: "14px", color: "#666", fontSize: "14px" }}>
          No notes created yet.
        </div>
      )}
    </div>
  );

  const renderForm = () => (
    <div style={sectionBox}>
      <div
        style={{
          fontWeight: "600",
          fontSize: "12px",
          color: "#004AAD",
          cursor: "pointer",
          marginBottom: "10px",
        }}
        onClick={goToList}
      >
        Back to Notes
      </div>

      <div style={{ fontWeight: 600, marginBottom: "12px" }}>
        {mode === "edit" ? "Edit Note" : "Create Note"}
      </div>

      <TextField
        fullWidth
        placeholder="Subject"
        variant="outlined"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        sx={{ mb: 2, background: "#F2F2F2" }}
      />

      <Box
        style={{
          borderRadius: "10px",
          backgroundColor: "#F2F2F2",
          marginBottom: "16px",
        }}
      >
        <FormControl fullWidth size="small">
          <Select
            sx={{ boxShadow: "none", ".MuiOutlinedInput-notchedOutline": { border: 0 } }}
            value={doctorType}
            displayEmpty
            onChange={(event) => setDoctorType(event.target.value)}
          >
            <MenuItem value="">Doctor Type</MenuItem>
            {doctorTypes.map((item) => (
              <MenuItem key={item} value={item}>
                {item}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <TextField
        fullWidth
        placeholder="Type your notes here..."
        variant="outlined"
        multiline
        rows={isMobile ? 6 : 8}
        value={content}
        onChange={(e) => setContent(e.target.value)}
        sx={{ mb: 2, background: "#F2F2F2" }}
      />

      <div style={actionRow}>
        <button type="button" style={primaryBtn} onClick={handleSave}>
          Save
        </button>
        <button type="button" style={secondaryBtn} onClick={goToList}>
          Cancel
        </button>
      </div>
    </div>
  );

  const renderView = () => (
    <div style={sectionBox}>
      <div
        style={{
          fontWeight: "600",
          fontSize: "12px",
          color: "#004AAD",
          cursor: "pointer",
          marginBottom: "10px",
        }}
        onClick={goToList}
      >
        Back to Notes
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          gap: "10px",
          flexDirection: isMobile ? "column" : "row",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: isMobile ? "18px" : "22px",
              fontWeight: 600,
              wordBreak: "break-word",
            }}
          >
            {activeNote?.title}
          </div>
          <div style={{ color: "#666", marginTop: "4px" }}>{activeNote?.doctor_type}</div>
        </div>

        {!isMobile && (
          <div style={actionRow}>
            <button type="button" style={secondaryBtn} onClick={openEdit}>
              Edit
            </button>
            <button type="button" style={dangerBtn} onClick={handleDelete}>
              Delete
            </button>
          </div>
        )}
      </div>

      <div style={noteTextBox}>{activeNote?.content}</div>

      {isMobile && (
        <div style={{ ...actionRow, marginTop: "12px" }}>
          <button type="button" style={secondaryBtn} onClick={openEdit}>
            Edit
          </button>
          <button type="button" style={dangerBtn} onClick={handleDelete}>
            Delete
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div style={wrapper}>
      <div
        style={{
          fontWeight: "600",
          fontSize: "12px",
          color: "#004AAD",
          cursor: "pointer",
          marginBottom: "8px",
        }}
        onClick={() => {
          setSelected(false);
          setExpand(true);
        }}
      >
        Back
      </div>

      <div
        style={{
          color: "#F0588B",
          fontWeight: "600",
          fontSize: isMobile ? "20px" : "24px",
          marginBottom: "14px",
        }}
      >
        Notes
      </div>

      {mode === "list" && renderList()}
      {(mode === "create" || mode === "edit") && renderForm()}
      {mode === "view" && renderView()}

      <AlertShow
        alertShowHide={showAlert}
        alertClose={handleCloseAlert}
        setalertMsg={alertMsg}
        severity={severity}
      />
    </div>
  );
}

export default Notes;
